import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@porchpass/ui";
import { Button } from "@porchpass/ui";
import { Image01Icon } from "@porchpass/ui";
import { useState, useRef } from "react";
import { useAuth } from "src/context";

interface LogoUploadDialogProps {
  dealerId: string;
}

export function LogoUploadDialog({ dealerId }: LogoUploadDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const MAX_DIMENSION = 200; // 200x200 pixels
  const ALLOWED_TYPES = ["image/png"];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setError(null);
    setSuccess(false);

    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Please select a PNG file.");
      setSelectedFile(null);
      return;
    }

    // Validate image dimensions
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        if (img.width > MAX_DIMENSION || img.height > MAX_DIMENSION) {
          setError(
            `Image must be ${MAX_DIMENSION}x${MAX_DIMENSION} pixels or smaller. Current size: ${img.width}x${img.height} pixels.`,
          );
          setSelectedFile(null);
          return;
        }
        setSelectedFile(file);
      };
      img.onerror = () => {
        setError("Failed to load image. Please ensure it's a valid PNG file.");
        setSelectedFile(null);
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !dealerId) {
      setError("Please select a file first.");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const token = await user.getIdToken();
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await fetch(
        `${__API_BASE_URL__}/admin/v1/dealers/${dealerId}/logo`,
        {
          method: "PUT",
          headers: {
            "x-jwt": token,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        let errorMessage = `Upload failed with status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData?.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // Use default error message
        }
        throw new Error(errorMessage);
      }

      setSuccess(true);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Close dialog after successful upload
      setTimeout(() => {
        setIsOpen(false);
        setSuccess(false);
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      setError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSelectedFile(null);
      setError(null);
      setSuccess(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="primary">
          <Image01Icon size={18} style={{ color: "white" }} />
          Upload Logo
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Logo</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription>
            Please input your logo file. Must be a .png and no larger than
            200x200 pixels.
          </DialogDescription>
          <div style={{ marginTop: "16px" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,image/png"
              onChange={handleFileSelect}
              disabled={isUploading}
              style={{ display: "none" }}
            />
            <Button
              variant="secondary"
              size="s"
              onClick={handleFileButtonClick}
              disabled={isUploading}
              style={{ marginBottom: "12px" }}
            >
              Choose File
            </Button>
            {selectedFile && (
              <p
                style={{ fontSize: "14px", color: "#666", margin: "8px 0 0 0" }}
              >
                Selected: {selectedFile.name}
              </p>
            )}
            {error && (
              <p
                style={{
                  fontSize: "14px",
                  color: "#d32f2f",
                  margin: "8px 0 0 0",
                }}
              >
                {error}
              </p>
            )}
            {success && (
              <p
                style={{
                  fontSize: "14px",
                  color: "#388e3c",
                  margin: "8px 0 0 0",
                }}
              >
                Logo uploaded successfully!
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="s" disabled={isUploading}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="s"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
